/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable import/no-cycle */
import Vue from 'vue';
import { defineModule } from 'direct-vuex';
import { shuffle, sortBy } from 'lodash';
import { moduleGetterContext, moduleActionContext } from '@/store';

import {
  Dataset,
  Annotation,
  AnnotationFrame,
  StudyPainScore,
  AnnotationFrameStatus,
  AnnotationScore,
  DatasetMetadata,
  MotionActionMap,
  TimeToFrameNumber,
  NewAnnotationFrame,
} from '@/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const datasetActionContext = (context: any) => moduleActionContext(context, datasetModule);
const datasetGetterContext = (
  (args: [unknown, unknown, unknown, unknown]) => moduleGetterContext(args, datasetModule)
);

interface CurrentFrameScorePayload {
  scoreClass: string;
  score: number;
}

interface CurrentFrameMotionScorePayload {
  actions: MotionActionMap;
}

interface CurrentFrameMotionStartTimePayload {
  startMinute: number;
  startSecond: number;
}

interface CurrentFrameMotionEndTimePayload {
  endMinute: number;
  endSecond: number;
}

interface NewMotionDatasetItemPayload {
  startMinute: number;
  startSecond: number;
  endMinute: number;
  endSecond: number;
  actions: MotionActionMap;
}

const datasetModule = defineModule({
  namespaced: true,
  state: {
    dataset: null as Dataset | null,
    userDataset: false,
    datasetId: null as string | null,
    studyType: null as string | null,
    validationOnly: null as boolean | null,
    loading: false,
    metadata: null as DatasetMetadata | null,
    currentFrameIndex: null as number | null,
    currentAnnotationIndex: null as number | null,
    drawingAnnotation: false,
  },
  getters: {
    currentFrame(...args): AnnotationFrame | null {
      const { state } = datasetGetterContext(args);
      const index = state.currentFrameIndex;

      if (index === null || state.dataset === null) {
        return null;
      }

      return state.dataset[index];
    },
    currentAnnotation(...args): Annotation | null {
      const { state } = datasetGetterContext(args);
      const { dataset, currentFrameIndex, currentAnnotationIndex } = state;

      let annotation = null;
      if (
        currentFrameIndex !== null
        && currentAnnotationIndex !== null
        && dataset !== null
        && currentAnnotationIndex < dataset[currentFrameIndex].annotations.regions.length
      ) {
        annotation = dataset[currentFrameIndex].annotations.regions[currentAnnotationIndex];
      }

      return annotation;
    },
    currentFrameAnnotations(...args): Annotation[] | null {
      const { state } = datasetGetterContext(args);
      const { dataset, currentFrameIndex } = state;

      if (dataset === null || currentFrameIndex === null) { return null; }
      return dataset[currentFrameIndex].annotations.regions;
    },
    currentPainScore(...args): StudyPainScore | null {
      const { getters: { currentFrame }, rootState: { application: { selectedStudyType } } } = datasetGetterContext(args);

      const currentScore = currentFrame?.score['pain-mgs'];
      return currentScore || null;
    },
    subjectType(...args): string | null {
      const { state } = datasetGetterContext(args);
      return state.metadata?.subject_race.toLowerCase() || null;
    },
  },
  mutations: {
    setDataset(state, dataset: Dataset | null) {
      state.dataset = dataset;
    },
    setUserDataset(state, user: boolean) {
      state.userDataset = user;
    },
    setDatasetId(state, id: string | null) {
      state.datasetId = id;
    },
    setStudyType(state, studyType: string | null) {
      state.studyType = studyType;
    },
    setValidationOnly(state, validationOnly: boolean | null) {
      state.validationOnly = validationOnly;
    },
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setMetadata(state, metadata: DatasetMetadata | null) {
      state.metadata = metadata;
    },
    setCurrentFrameIndex(state, index: number | null) {
      state.currentFrameIndex = index;
    },
    setCurrentAnnotationIndex(state, index: number | null) {
      state.currentAnnotationIndex = index;
    },
    setDrawingAnnotation(state, drawing: boolean) {
      state.drawingAnnotation = drawing;
    },

    // Extra mutations
    setFrameImageFile(state, payload: { index: number; file: File }) {
      if (state.dataset === null) {
        throw new Error('Cannot set image file for frame in null dataset!');
      }

      const { index, file } = payload;
      state.dataset[index].file = file;
    },
    updateCurrentAnnotation(state, annotation: Annotation) {
      const { dataset, currentFrameIndex, currentAnnotationIndex } = state;
      if (
        currentFrameIndex === null
        || currentAnnotationIndex === null
        || dataset === null
      ) { return; }

      Vue.set(dataset[currentFrameIndex].annotations.regions, currentAnnotationIndex, annotation);
    },
    addAnnotation(state, payload: { frame: number; annotation: Annotation }) {
      const { frame, annotation } = payload;
      const { dataset } = state;
      if (dataset === null) {
        return;
      }

      const newLength = dataset[frame].annotations.regions.push(annotation);
      state.currentAnnotationIndex = newLength - 1;
    },
    removeAnnotation(state, index: number | null) {
      const { dataset, currentFrameIndex } = state;
      if (currentFrameIndex === null || dataset === null || index === null) { return; }

      Vue.delete(dataset[currentFrameIndex].annotations.regions, index);
    },
    setFrameScore(state, payload: { frame: number; score: AnnotationScore }) {
      const { frame, score } = payload;
      if (state.dataset !== null) {
        state.dataset[frame].score = score;
      }
    },
    updateFrame(state, { index, frame }: { index: number; frame: AnnotationFrame }) {
      if (state.dataset === null) {
        throw new Error('Cannot update frame of null dataset!');
      }

      Vue.set(state.dataset, index, frame);
    },
  },
  actions: {
    /**
     * FRAME ACTIONS
     */

    changeFrame(context, frame: number) {
      const { commit, state } = datasetActionContext(context);
      const { dataset, currentFrameIndex } = state;
      if (dataset === null || currentFrameIndex === null) { return; }

      commit.setCurrentFrameIndex(frame);
    },

    async deleteFrame(context, index: number) {
      const { state, rootState } = datasetActionContext(context);
      const { client } = rootState;
      const { dataset, userDataset } = state;

      if (dataset === null || client === null) { return; }
      const frame = dataset[index];
      await client.deleteDatasetItem(frame, userDataset);
    },

    async setFrameStatus(context, { index, status }: { index: number; status: AnnotationFrameStatus }) {
      const { commit, dispatch, state, rootState } = datasetActionContext(context);
      const { client } = rootState;
      const { dataset, userDataset } = state;

      if (dataset === null || client === null) { return; }
      let newFrame;
      const frame = dataset[index];

      commit.setLoading(true);
      try {
        newFrame = await client.setDatasetItemStatus(frame, status, userDataset);
      } finally {
        commit.setLoading(false);
      }

      commit.updateFrame({ index, frame: newFrame });
      dispatch.setFramePredicted({ index, predicted: false });
    },

    /**
     * Set whether a given frame is a result of running prediction, or not.
     */
    setFramePredicted(context, payload: { index: number; predicted: boolean }) {
      const { commit, state: { dataset }, rootState: { client } } = datasetActionContext(context);
      if (dataset === null || !client?.user) {
        throw new Error('Cannot set prediction status with null dataset or client!');
      }

      const { index, predicted } = payload;
      const scorer = predicted ? 'model' : client.user.username;

      const currentFrame = dataset[index];
      const frame: AnnotationFrame = {
        ...currentFrame,
        reference: {
          ...currentFrame.reference,
          scorer,
        },
      };

      commit.updateFrame({ index, frame });
    },

    async saveCurrentFrame(context) {
      const {
        dispatch,
        getters: { currentFrame },
        state: { currentFrameIndex, userDataset },
        rootState: { client },
      } = datasetActionContext(context);
      if (client === null || currentFrameIndex === null) { return; }

      // Update prediction status before saving
      dispatch.setFramePredicted({ index: currentFrameIndex, predicted: false });

      if (currentFrame === null) {
        throw new Error('currentFrame unexpectedly null!');
      }

      await client.saveDatasetItem(currentFrame, userDataset);
    },

    async saveNewDatasetItem(context, payload: NewMotionDatasetItemPayload) {
      const {
        dispatch,
        getters: { currentFrame },
        state: { currentFrameIndex, userDataset },
        rootState: { client },
      } = datasetActionContext(context);
      if (client === null || currentFrameIndex === null) { return; }

      const { _id, ...rest } = Object.assign({}, currentFrame); // eslint-disable-line prefer-object-spread

      const { startMinute, startSecond, endMinute, endSecond, actions } = payload;
      const frameRate = currentFrame?.reference.fps!;
      const newStartFrame = TimeToFrameNumber(startMinute, startSecond, frameRate);
      const newEndFrame = TimeToFrameNumber(endMinute, endSecond, frameRate);

      const newMotionScore = {
        ...currentFrame?.score.motion!,
        start_frame: newStartFrame,
        end_frame: newEndFrame,
        actions,
      };

      const newTopLevelScore = {
        ...currentFrame?.score,
        motion: newMotionScore,
      };

      const newReference = {
        ...currentFrame?.reference,
        frame_index: newStartFrame,
      };

      const videoName = currentFrame?.reference.video_filename!;
      const indexPadded = String(newStartFrame).padStart(8, '0');
      const newImagePath = `${videoName}/${indexPadded}`;

      const newDatasetItem: NewAnnotationFrame = {
        ...currentFrame!,
        image_path: newImagePath,
        score: newTopLevelScore,
        reference: newReference,
      };

      delete newDatasetItem._id; // eslint-disable-line no-underscore-dangle

      if (currentFrame === null) {
        throw new Error('currentFrame unexpectedly null!');
      }

      await client.saveNewDatasetItem(newDatasetItem, userDataset);
    },

    /**
     * SCORE ACTIONS
     */

    clearScore(context, index: number) {
      const {
        state: { dataset },
        dispatch,
        commit,
        rootState: { application: { selectedStudyType } },
      } = datasetActionContext(context);

      const frame = dataset?.[index];
      if (dataset === null || frame === undefined) {
        throw new Error('Invalid dataset or frame index!');
      }

      const selectedScore = frame.score[selectedStudyType as keyof AnnotationScore];
      if (selectedScore === undefined) {
        throw new Error('No score for selected study type!');
      }

      // Initialize all fields to -1
      const newScore = Object.keys(selectedScore)
        .reduce((obj, key) => ({ ...obj, [key]: -1 }), {});

      // Copy existing frame and inject new score
      const newFrame: AnnotationFrame = {
        ...frame,
        score: { ...frame.score, [selectedStudyType]: newScore },
      };

      // Update frame and prediction status
      commit.updateFrame({ index, frame: newFrame });
      dispatch.setFramePredicted({ index, predicted: false });
    },

    setCurrentFramePainScore(context, payload: CurrentFrameScorePayload) {
      const { dispatch, commit, state, rootState } = datasetActionContext(context);
      const { dataset, currentFrameIndex } = state;

      if (dataset !== null && currentFrameIndex !== null) {
        const { scoreClass, score } = payload;
        const { selectedStudyType } = rootState.application;

        const newStudyTypeScore = {
          ...dataset[currentFrameIndex].score[selectedStudyType as keyof AnnotationScore],
          [scoreClass]: score,
        };
        const newTopLevelScore = {
          ...dataset[currentFrameIndex].score,
          'pain-mgs': newStudyTypeScore,
        };

        // Update score and prediction status
        commit.setFrameScore({ frame: currentFrameIndex, score: newTopLevelScore });
        dispatch.setFramePredicted({ index: currentFrameIndex, predicted: false });
      }
    },

    setCurrentFrameMotionScore(context, payload: CurrentFrameMotionScorePayload) {
      const { dispatch, commit, state, rootState } = datasetActionContext(context);
      const { dataset, currentFrameIndex } = state;

      if (dataset !== null && currentFrameIndex !== null) {
        const { actions } = payload;

        const newMotionScore = {
          ...dataset[currentFrameIndex].score.motion!,
          actions,
        };

        const newTopLevelScore = {
          ...dataset[currentFrameIndex].score,
          motion: newMotionScore,
        };

        // Update score and prediction status
        commit.setFrameScore({ frame: currentFrameIndex, score: newTopLevelScore });
        dispatch.setFramePredicted({ index: currentFrameIndex, predicted: false });
      }
    },

    setCurrentFrameMotionStartFrame(context, payload: CurrentFrameMotionStartTimePayload) {
      const { dispatch, commit, state, rootState } = datasetActionContext(context);
      const { dataset, currentFrameIndex } = state;

      if (dataset !== null && currentFrameIndex !== null) {
        const { startMinute, startSecond } = payload;
        const frameRate = dataset[currentFrameIndex].reference.fps;
        const newStartFrame = TimeToFrameNumber(startMinute, startSecond, frameRate);

        const newMotionScore = {
          ...dataset[currentFrameIndex].score.motion!,
          start_frame: newStartFrame,
        };

        const newTopLevelScore = {
          ...dataset[currentFrameIndex].score,
          motion: newMotionScore,
        };

        // Update score and prediction status
        commit.setFrameScore({ frame: currentFrameIndex, score: newTopLevelScore });
        dispatch.setFramePredicted({ index: currentFrameIndex, predicted: false });
      }
    },

    setCurrentFrameMotionEndFrame(context, payload: CurrentFrameMotionEndTimePayload) {
      const { dispatch, commit, state, rootState } = datasetActionContext(context);
      const { dataset, currentFrameIndex } = state;

      if (dataset !== null && currentFrameIndex !== null) {
        const { endMinute, endSecond } = payload;
        const frameRate = dataset[currentFrameIndex].reference.fps;
        const newEndFrame = TimeToFrameNumber(endMinute, endSecond, frameRate);

        const newMotionScore = {
          ...dataset[currentFrameIndex].score.motion!,
          end_frame: newEndFrame,
        };

        const newTopLevelScore = {
          ...dataset[currentFrameIndex].score,
          motion: newMotionScore,
        };

        // Update score and prediction status
        commit.setFrameScore({ frame: currentFrameIndex, score: newTopLevelScore });
        dispatch.setFramePredicted({ index: currentFrameIndex, predicted: false });
      }
    },

    /**
     * DATASET ACTIONS
     */

    // Clears all scores for the entire dataset
    clearDatasetScores(context) {
      const { state: { dataset }, dispatch } = datasetActionContext(context);
      if (dataset === null) {
        throw new Error('Cannot clear scores on null dataset!');
      }

      dataset.forEach((_, index) => {
        dispatch.clearScore(index);
      });
    },

    shuffleDataset(context) {
      const { commit, state: { dataset } } = datasetActionContext(context);

      if (dataset === null) {
        throw new Error('Cannot shuffle null dataset!');
      }

      const shuffledDataset = shuffle(dataset);
      commit.setDataset(shuffledDataset);
      commit.setCurrentFrameIndex(0);
    },

    sortDataset(context) {
      const { commit, state: { dataset } } = datasetActionContext(context);

      if (dataset === null) {
        throw new Error('Cannot sort null dataset!');
      }

      const sortedDataset = sortBy(dataset, ['image_path']);
      commit.setDataset(sortedDataset);
      commit.setCurrentFrameIndex(0);
    },

    clearDataset(context) {
      const { commit } = datasetActionContext(context);

      commit.setMetadata(null);
      commit.setDataset(null);
      commit.setUserDataset(false);
      commit.setDatasetId(null);
      commit.setValidationOnly(null);
      commit.setCurrentFrameIndex(null);
      commit.setCurrentAnnotationIndex(null);
      commit.setLoading(false);
      commit.setDrawingAnnotation(false);
    },

    async saveCurrentDataset(context) {
      const {
        state: { dataset, userDataset },
        rootState: { client },
      } = datasetActionContext(context);
      if (client === null || dataset === null) { return; }

      // FIXME: Should return value here, but get typescript issues when doing that.
      await client.saveRemoteDataset(dataset, userDataset);
    },

    async setDatasetStatus(context, status: AnnotationFrameStatus) {
      const { commit, state, rootState } = datasetActionContext(context);
      const { client } = rootState;
      const { dataset, datasetId, userDataset } = state;

      if (dataset === null || datasetId === null || client === null) { return; }
      let newDataset: Dataset;

      commit.setLoading(true);
      try {
        if (status === 'confirmed') {
          newDataset = await client.confirmDataset(dataset, datasetId, userDataset);
        } else {
          newDataset = await client.unconfirmDataset(dataset, datasetId, userDataset);
        }
        commit.setDataset(newDataset);
      } finally {
        commit.setLoading(false);
      }
    },

    async setValidationOnly(context, validation_only: boolean) {
      const { commit, state, rootState } = datasetActionContext(context);
      const { client } = rootState;
      const { dataset, datasetId, userDataset } = state;

      if (dataset === null || datasetId === null || client === null) { return; }
      let newDataset: Dataset;

      commit.setLoading(true);
      try {
        await client.updateValidationOnlyRemoteDataset(datasetId, validation_only, userDataset);
        newDataset = await client.getRemoteDataset(datasetId, false);
        commit.setDataset(newDataset);
        commit.setValidationOnly(validation_only);
      } finally {
        commit.setLoading(false);
      }
    },
  },
  modules: {},
});

export default datasetModule;
